<template>
  <div>
    <v-row class="align-center">
      <v-col class="d-flex align-center">
        <SearchTextField :model-value="searchQuery"
                         :tooltipText="$t('project.members.MembersHeader.searchPlaceholder')"
                         data-locator="participants-search-input"
                         @update:model-value="$emit('update:searchQuery', $event)"
        />
        <span v-if="hideGroups && isCurrentUserPm" class="mx-1">
          <AppTooltip>
            <template #activator="{ props }">
              <v-chip
                class="hidden-groups-chip"
                variant="flat"
                label
                size="small"
                v-bind="props"
              >
                <span class="body-md-bold text-tertiary">{{ $t('project.members.MembersHeader.hiddenGroups') }}</span>
                <app-material-icon name="lock" :size="14" color="icon-blue" class="ml-2" />
              </v-chip>
            </template>
            <span>{{$t('project.members.MembersHeader.hideGroupsTooltip')}}</span>
          </AppTooltip>
        </span>
      </v-col>
      <v-col v-if="isCurrentUserPm"
             cols="auto"
      >
        <app-toggle
          :model-value="hideGroups"
          class="mt-n1"
          :label="$t('project.members.MembersHeader.hideGroupsLabel')"
          hide-details
          :loading="patchHideGroupsLoading"
          @click.prevent="!patchHideGroupsLoading && hideGroupsHandler()"
        />
      </v-col>
      <v-col v-if="isCurrentUserPm"
             cols="auto"
      >
        <v-menu location="bottom" max-width="300">
          <template #activator="{ props }">
            <app-button type="outlined"
                        variant="neutral"
                        v-bind="props"
            >
              {{$t('project.members.MembersHeader.options')}}
              <app-material-icon name="keyboard_arrow_down" />
            </app-button>
          </template>
          <v-list data-locator="participants-options-menu">
            <v-list-item v-if="isCurrentUserPm"
                         link
                         @click="importModalIsOpen = true"
            >
            <template #prepend>
              <div class="mr-2 member-header-title-icon">
                <app-material-icon name="draft" />
              </div>
            </template>
              <v-list-item-title class="text-text-secondary">{{ $t('project.members.MembersHeader.import') }}</v-list-item-title>

            </v-list-item>
            <AppDownloadFile :link="massExportAsExcelLink" class="d-block">
              <template #default="{ submitForm }">
                <v-list-item link @click="submitForm">
                  <template #prepend>
                    <div class="mr-2 member-header-title-icon">
                      <app-material-icon name="file_export" size="medium" />
                    </div>
                  </template>

                <v-list-item-title class="text-text-secondary">{{ $t('project.members.MembersHeader.massExportAsExcel') }}</v-list-item-title>

                </v-list-item>
              </template>
            </AppDownloadFile>
            <v-divider class="mx-4"></v-divider>
            <AppDownloadFile :link="exportAsExcelLink" class="d-block">
              <template #default="{ submitForm }">
                <v-list-item link @click="submitForm">
                  <template #prepend>
                    <app-material-icon name="file_export" class="mr-2 member-header-title-icon" />
                  </template>

                  <v-list-item-title class="text-text-secondary">
                    {{ $t('project.members.MembersHeader.exportAsExcel') }}
                  </v-list-item-title>
                </v-list-item>
              </template>
            </AppDownloadFile>
            <AppDownloadFile :link="exportAsWordLink" class="d-block">
              <template #default="{ submitForm }">
                <v-list-item link @click="submitForm">
                  <template #prepend>
                    <app-material-icon name="file_export" class="mr-2 member-header-title-icon" />
                  </template>

                  <v-list-item-title class="text-text-secondary">
                    {{ $t('project.members.MembersHeader.exportAsWord') }}
                  </v-list-item-title>
                </v-list-item>
              </template>
            </AppDownloadFile>
            <AppDownloadFile v-if="hasNda" :link="exportSignedNdaLink" :isNewTab="false" class="d-block">
              <template #default="{ submitForm }">
                <v-list-item
                  data-locator="download-all-nda-menu-item"
                  link
                  @click="onDownloadSignedNda(submitForm)"
                >
                  <template #prepend>
                    <span class="mr-2 member-header-title-icon">
                      <app-material-icon name="download" />
                    </span>
                  </template>
                    <v-list-item-title class="text-text-secondary" style="white-space: normal">
                      {{ $t('project.members.MembersHeader.downloadSignedNda') }}
                    </v-list-item-title>
                </v-list-item>
              </template>
            </AppDownloadFile>
            <v-divider class="mx-4"></v-divider>
            <v-list-item
              v-if="isCurrentUserPm"
              link
              @click="sendGlobalReminder"
            >
              <template #prepend>
                <app-material-icon name="mail" />
              </template>
              <v-list-item>
                <v-list-item-title class="text-text-secondary">
                  {{ $t('project.members.MembersHeader.sendGlobalReminder') }}
                  <div class="detail-md-regular text-text-tertiary mt-2"  style="white-space: normal;">
                    {{ $t('project.members.MembersHeader.sendGlobalReminderSubtitle') }}
                  </div>
                </v-list-item-title>
              </v-list-item>
            </v-list-item>
          </v-list>
        </v-menu>

      </v-col>
      <v-col v-if="isCurrentUserPm" cols="auto">
        <app-button color="primary" data-locator="add-group-main-button" @click="openAddGroupDialog">
          {{ $t('project.members.MembersHeader.addGroup') }}
        </app-button>
      </v-col>
    </v-row>

    <app-modal v-model="openHideGroupsModal"
               size="xs"
               @cancel="openHideGroupsModal = false"
               @ok="onConfirmHideGroups"
    >
      <template #title>{{$t('project.members.MembersHeader.hideGroupsModal.title')}}</template>
      <template #body>
        {{$t('project.members.MembersHeader.hideGroupsModal.text')}}
      </template>
    </app-modal>

    <GroupImportDialog
      v-if="importModalIsOpen"
      :mnemo="mnemo"
      @close="importModalIsOpen = false"
    />
  </div>
</template>

<script>
import axios from 'axios'
import { mapActions, mapGetters, mapState } from 'vuex'

import AppDownloadFile from '@/common/AppDownloadFile.vue'
import AppTooltip from '@/common/AppTooltip.vue'
import SearchTextField from '@/common/SearchTextField.vue'
import { toast } from '@/common/utils/toast'
import { SET_CURRENT_ROOM } from '@/store/modules/room/mutation_types'

import GroupImportDialog from './dialogs/GroupImportDialog.vue'

export default {
  name: 'MembersHeader',
  components: {
    AppDownloadFile,
    GroupImportDialog,
    SearchTextField,
    AppTooltip,
  },
  props: {
    searchQuery: {
      type: String,
      required: true,
    },
    mnemo: {
      type: String,
      required: true,
    },
  },
  data () {
    return {
      openHideGroupsModal: false,
      importModalIsOpen: false,
    }
  },
  computed: {
    ...mapState('groups', ['patchHideGroupsLoading']),
    ...mapState('room', ['currentRoom']),
    ...mapGetters('room', ['isCurrentUserPm', 'roomMnemo']),
    massExportAsExcelLink () {
      return `${import.meta.env.VUE_APP_API_URL}/room/${this.mnemo}/downloads/participants-excel-mass`
    },
    hasNda () {
      return this.currentRoom?.hasNda
    },
    exportAsExcelLink () {
      return `${import.meta.env.VUE_APP_API_URL}/room/${this.mnemo}/downloads/participants-excel`
    },
    exportAsWordLink () {
      return `${import.meta.env.VUE_APP_API_URL}/room/${this.mnemo}/downloads/participants-word`
    },
    exportSignedNdaLink () {
      return `${import.meta.env.VUE_APP_API_URL}/room/${this.mnemo}/downloads/signed-nda`
    },
    hideGroups () {
      return this.currentRoom.hideGroups === 1
    },
  },
  methods: {
    ...mapActions('groups', ['PATCH_HIDE_GROUPS', 'SEND_GLOBAL_REMINDER']),
    async sendGlobalReminder () {
      try {
        await this.SEND_GLOBAL_REMINDER({
          mnemo: this.roomMnemo,
        })
        toast.success(this.$t('project.members.MembersHeader.sendGlobalReminderSuccess'))
      } catch (error) {
        toast.error(this.$t('project.members.MembersHeader.sendGlobalReminderError'))
      }
    },
    openAddGroupDialog () {
      this.$emit('onAddGroup')
    },
    async onDownloadSignedNda (callback) {
      if (!this.hasNda) return
      try {
        // Check that file is available, then submit form
        await axios.head(this.exportSignedNdaLink)
        callback()
        toast.success(this.$t('project.members.MembersHeader.downloadComplete'))
      } catch (error) {
        console.error(error)
        toast.error(this.$t('project.members.MembersHeader.downloadError'))
      }
    },
    hideGroupsHandler () {
      if (this.hideGroups) {
        this.openHideGroupsModal = true
      } else {
        this.PATCH_HIDE_GROUPS({ hideGroupsStatus: 'true' })
          .then(() => {
            toast.success(this.$t('project.members.MembersHeader.messages.hideGroups'))
            this.currentRoom.hideGroups = 1
            this.$store.commit(`room/${SET_CURRENT_ROOM}`, this.currentRoom)
          })
      }
    },
    onConfirmHideGroups () {
      this.PATCH_HIDE_GROUPS({ hideGroupsStatus: 'false' })
        .then(() => {
          toast.success(this.$t('project.members.MembersHeader.messages.notHideGroups'))
          this.currentRoom.hideGroups = 0
          this.$store.commit(`room/${SET_CURRENT_ROOM}`, this.currentRoom)
          this.openHideGroupsModal = false
        })
    },
  },
}
</script>

<style scoped lang="scss">
@import "@/scss/border-tokens";

.member-header-title-icon{
  width: 25px;
}

.hidden-groups-chip {
  background-color: rgb(var(--v-theme-surface-blue-subtle));
  color: rgb(var(--v-theme-text-blue));
  border: get-border('border-sky');
  @include border-radius('border-radius-pill');
}
</style>
